import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Container, Box, Typography, TextField, Button,
  Paper, Alert, CircularProgress, Chip, Stack
} from '@mui/material';
import { validateUsername, sanitizeUsername, generateUsernameSuggestions } from '../utils/usernameUtils';
import apiConfig from '../services/apiConfig';

const ChooseUsername = () => {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // null | 'checking' | 'available' | 'taken'
  const [usernameStatus, setUsernameStatus] = useState(null);
  const usernameTimer = useRef(null);
  const navigate = useNavigate();

  // Generate suggestions from the user's Google name
  useEffect(() => {
    const loadSuggestions = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const name = currentUser.attributes?.name || '';
        if (name) {
          setSuggestions(generateUsernameSuggestions(name, '').slice(0, 4));
        }
      } catch {
        navigate('/login', { replace: true });
      }
    };
    loadSuggestions();
  }, [navigate]);

  const checkUsername = (value) => {
    if (value.length < 3) { setUsernameStatus(null); return; }
    setUsernameStatus('checking');
    clearTimeout(usernameTimer.current);
    usernameTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${apiConfig.core}/api/public/check-username?username=${encodeURIComponent(value)}`);
        const data = await res.json();
        setUsernameStatus(data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus(null);
      }
    }, 400);
  };

  const handleChange = (e) => {
    const sanitized = sanitizeUsername(e.target.value);
    setUsername(sanitized);
    setError('');
    checkUsername(sanitized);
  };

  const handleSuggestionClick = (suggestion) => {
    setUsername(suggestion);
    setError('');
    checkUsername(suggestion);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateUsername(username);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }
    if (usernameStatus === 'taken') {
      setError('That username is already taken. Please choose a different one.');
      return;
    }

    setLoading(true);
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // 1. Server check + DynamoDB save FIRST (before touching Cognito)
      //    Server uses self-excluding check so re-entry with own username works.
      const saveRes = await fetch(`${apiConfig.core}/api/roles/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username }),
      });

      if (!saveRes.ok) {
        const data = await saveRes.json().catch(() => ({}));
        if (saveRes.status === 409) {
          setUsernameStatus('taken'); // keep helper text consistent with alert
        }
        throw new Error(data.error || 'Failed to save username. Please try again.');
      }

      // 2. Only set preferred_username in Cognito after the server confirmed it's free
      await Auth.updateUserAttributes(currentUser, {
        preferred_username: username,
      });

      navigate('/add-role', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to save username. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Choose a Username
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Pick a unique username for your InfHire account. Others will find you as{' '}
            <strong>@{username || 'yourname'}</strong>.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={handleChange}
              placeholder="e.g., john_fitness_coach"
              error={usernameStatus === 'taken'}
              helperText={
                usernameStatus === 'checking' ? '⏳ Checking availability…' :
                usernameStatus === 'available' ? '✓ Username is available' :
                usernameStatus === 'taken' ? '✗ Username is already taken' :
                'Lowercase letters, numbers and underscores only (3–30 characters)'
              }
              FormHelperTextProps={{
                sx: {
                  color: usernameStatus === 'available' ? 'success.main' :
                         usernameStatus === 'taken' ? 'error.main' : undefined,
                }
              }}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>@</Box>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {suggestions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Suggestions:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, gap: 1 }}>
                  {suggestions.map((s) => (
                    <Chip
                      key={s}
                      label={`@${s}`}
                      size="small"
                      onClick={() => handleSuggestionClick(s)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !username || usernameStatus === 'checking' || usernameStatus === 'taken'}
              sx={{ mt: 1 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChooseUsername;
